import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from './client';
import { useAuth } from '@/lib/auth/AuthProvider';
import {
  adminSessionHeaders,
  membershipInvite,
} from '@/features/membership/membershipLinks';

export interface MembershipOfferPlan {
  /** The Stripe subscription id (MeSubscription.stripe_subscription_id). */
  id: string;
  /** Its next renewal at the member price. */
  renewal_cents: number;
  /** What the join perk takes off that renewal. */
  perk_cents: number;
}

/** GET /me/membership/offer: the $199 System Membership for an existing patient. */
export interface MembershipOffer {
  available: boolean;
  /** In the A/B population (both arms): a renewing plan, none past due. */
  eligible: boolean;
  reason: 'unavailable' | 'member' | 'past_due' | 'no_subscription' | 'not_offered' | null;
  /** portal = the A/B offer; rep_link / admin = sold by a care rep. */
  source: 'portal' | 'rep_link' | 'admin';
  /** The rep enrolling, only in their own View-as-patient session. */
  rep: string | null;
  experiment_key: string | null;
  variant_key: string | null;
  price_cents: number;
  discount_percent: number;
  lab_interval_months: number;
  lab_annual_value_cents: number;
  perk_percent: number;
  subscriptions: MembershipOfferPlan[];
}

export function useMembershipOffer(enabled = true) {
  const { session } = useAuth();
  const invite = membershipInvite();
  return useQuery({
    queryKey: ['me', 'membership-offer', session?.user.id, invite],
    queryFn: () =>
      apiFetch<MembershipOffer>(
        `/me/membership/offer${invite ? `?invite=${encodeURIComponent(invite)}` : ''}`,
        { headers: adminSessionHeaders() },
      ),
    enabled: enabled && Boolean(session?.user.id),
    staleTime: 60_000,
  });
}

export interface MembershipPurchaseBody {
  request_id: string;
  consent: true;
  expected_amount_cents: number;
  subscription_id: string | null;
  payment_method_id: string | null;
  wallet_payment_method_id: string | null;
  invite: string | null;
}

export interface MembershipPurchaseResult {
  /** The attempt this answer belongs to; retries reuse it. */
  request_id?: string | null;
  status: 'active' | 'requires_action' | 'processing';
  client_secret?: string | null;
  /** processing: true once the fee is paid and setup is finishing. */
  paid?: boolean;
  perk_cents?: number | null;
}

export function usePurchaseMembership() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MembershipPurchaseBody) =>
      apiFetch<MembershipPurchaseResult>('/me/membership/purchase', {
        method: 'POST',
        headers: adminSessionHeaders(),
        body: JSON.stringify(body),
      }),
    onSuccess: (result) => {
      if (result.status !== 'active') return;
      // Member pricing, the membership card and the perk on the plan.
      void qc.invalidateQueries({ queryKey: ['bootstrap'] });
      void qc.invalidateQueries({ queryKey: ['marketplace'] });
      void qc.invalidateQueries({ queryKey: ['me', 'subscriptions'] });
      void qc.invalidateQueries({ queryKey: ['me', 'membership-offer'] });
    },
  });
}
